import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requireSystemAdmin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles = [],
  requireSystemAdmin = false,
}) => {
  const { user, role, isLoading, isSystemAdmin, requiresPasswordReset } = useAuth();
  const location = useLocation();

  // Wait for auth state to be fully loaded, including role data
  // If user exists but role is still null, keep loading
  if (isLoading || (user && role === null)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    console.log('ProtectedRoute: No user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Check if user needs to reset password before accessing protected routes
  // Only check this after auth is fully loaded to prevent redirect loops
  // Don't redirect if already on the forced password reset page
  if (!isLoading && requiresPasswordReset && location.pathname !== '/forced-password-reset') {
    console.log('ProtectedRoute: User requires password reset, redirecting');
    return <Navigate to="/forced-password-reset" replace />;
  }

  if (requireSystemAdmin && !isSystemAdmin) {
    console.log('ProtectedRoute: Not system admin, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  if (allowedRoles.length > 0 && role && !allowedRoles.includes(role)) {
    console.log('ProtectedRoute: Role not allowed, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};
